import * as Popover from "@radix-ui/react-popover";
import { CalendarDays } from "lucide-react";
import { DayPicker, type DateRange, type Matcher } from "react-day-picker";
import { cn } from "../../lib/cn";

interface DateRangePickerProps {
	value: DateRange | undefined;
	onChange: (range: DateRange | undefined) => void;
	placeholder?: string;
	min?: Date | null;
	max?: Date | null;
	disabled?: boolean;
}

const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

const describe = (range: DateRange | undefined, placeholder: string): string => {
	if (!range?.from) return placeholder;
	if (!range.to) return `${formatter.format(range.from)} — …`;
	return `${formatter.format(range.from)} — ${formatter.format(range.to)}`;
};

export const DateRangePicker = ({
	value,
	onChange,
	placeholder = "Pick a date range",
	min,
	max,
	disabled = false,
}: DateRangePickerProps) => {
	const blocked: Matcher[] = [];
	if (min) blocked.push({ before: min });
	if (max) blocked.push({ after: max });

	return (
		<Popover.Root>
			<Popover.Trigger
				disabled={disabled}
				className={cn(
					"flex h-9 w-full max-w-sm items-center gap-2 rounded-md border border-line bg-surface px-3 text-left text-sm",
					"hover:border-line-strong focus:outline-none",
					disabled && "cursor-not-allowed opacity-50 hover:border-line",
					value?.from ? "text-ink" : "text-ink-faint",
				)}
			>
				<CalendarDays className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
				{describe(value, placeholder)}
			</Popover.Trigger>

			<Popover.Portal>
				<Popover.Content
					align="start"
					sideOffset={6}
					className="z-50 rounded-lg border border-line bg-surface p-3 shadow-xl focus:outline-none"
				>
					<DayPicker
						mode="range"
						numberOfMonths={2}
						selected={value}
						onSelect={onChange}
						defaultMonth={value?.from ?? min ?? undefined}
						disabled={blocked.length > 0 ? blocked : undefined}
						startMonth={min ?? undefined}
						endMonth={max ?? undefined}
					/>
					<div className="mt-2 flex justify-end border-t border-line pt-2">
						<button
							type="button"
							onClick={() => onChange(undefined)}
							className="rounded-md px-2 py-1 text-xs text-ink-muted transition-colors hover:bg-raised hover:text-ink"
						>
							Clear
						</button>
					</div>
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
};
